import React, { useState } from 'react';
import { Truck, Lock, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from './ui/LoadingSpinner';

export default function ResetPasswordPage({ onBackToLogin }) {
  const { updatePassword, loading, error } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [localError, setLocalError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');

    if (!password) {
      setLocalError('Please enter a new password');
      return;
    }

    if (password.length < 6) {
      setLocalError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    try {
      await updatePassword(password);
      setSuccess(true);
    } catch (err) {
      setLocalError(err.message);
    }
  };

  const displayError = localError || error;

  if (success) {
    return (
      <div className="min-h-screen bg-locate2-bg-light flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-md text-center">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-status-success-bg rounded-full flex items-center justify-center">
              <CheckCircle size={32} className="text-status-success" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-locate2-dark-blue mb-4">
            Password Updated!
          </h1>
          <p className="text-locate2-light-gray mb-6">
            Your password has been successfully updated. You can now sign in with your new password.
          </p>
          <button
            onClick={onBackToLogin}
            className="w-full bg-locate2-primary text-white rounded-lg px-4 py-3 font-medium hover:bg-locate2-dark-blue transition-all"
          >
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-locate2-bg-light flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="w-10 h-10 bg-locate2-primary rounded-lg flex items-center justify-center">
              <Truck size={24} className="text-white" />
            </div>
            <span className="text-2xl font-bold text-locate2-dark-blue">Locate2u</span>
          </div>
          <h1 className="text-2xl font-bold text-locate2-dark-blue mb-2">
            Reset Your Password
          </h1>
          <p className="text-locate2-light-gray">
            Enter your new password below
          </p>
        </div>

        {displayError && (
          <div className="mb-4 p-3 bg-status-error-bg text-status-error rounded-lg text-sm">
            {displayError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-locate2-medium-gray mb-1">
              New Password
            </label>
            <div className="relative">
              <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-locate2-light-gray" />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-3 border-2 border-locate2-border rounded-lg focus:border-locate2-primary focus:outline-none transition-colors"
                placeholder="Enter new password"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-locate2-light-gray hover:text-locate2-medium-gray"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-locate2-medium-gray mb-1">
              Confirm New Password
            </label>
            <div className="relative">
              <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-locate2-light-gray" />
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-3 border-2 border-locate2-border rounded-lg focus:border-locate2-primary focus:outline-none transition-colors"
                placeholder="Confirm new password"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-locate2-light-gray hover:text-locate2-medium-gray"
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-locate2-primary text-white rounded-lg px-4 py-3 font-medium hover:bg-locate2-dark-blue transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? <LoadingSpinner size="sm" className="border-white border-t-transparent" /> : null}
            {loading ? 'Updating...' : 'Update Password'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={onBackToLogin}
            className="text-locate2-primary hover:text-locate2-dark-blue transition-colors text-sm"
          >
            Back to Sign In
          </button>
        </div>
      </div>
    </div>
  );
}
